/**
 * Phase 6e.2 — thin POST wrapper around `/api/fx/rates`.
 *
 * Uses the existing `apiFetch` plumbing (cold-start detection,
 * timeout, structured `ApiError`) so the FX sync inherits the same
 * error semantics as `/api/calculate` and `/api/engagements/calculate`.
 * The Magic Button (Phase 6e.3) will catch `ApiError` from this
 * client and surface it via the usual admonition path.
 */

import { apiFetch } from '@/api/client';

import type { FxRatesApiResponse } from './types';

export interface FxRatesRequest {
  /** YYYY-MM-DD inclusive lower bound. */
  startDate: string;
  /** YYYY-MM-DD inclusive upper bound. */
  endDate: string;
  /**
   * ISO 4217 codes — e.g. `["USD", "EUR", "GBP"]`.
   *
   * Phase 6e.4.1 — OPTIONAL. Omit (or pass `undefined`) to receive
   * every currency present in TCMB's daily XML. The engagement
   * closing-rates accordion uses this path so one HTTP call yields
   * every rate the auditor might need.
   */
  currencies?: string[];
  /**
   * Phase 6e.4.4 — engagement functional currency. When present and
   * !== "TRY" (case-insensitive), the backend applies IAS 21 §B6
   * cross-rates
   *   X/F   = (X/TRY) / (F/TRY)
   *   TRY/F = 1 / (F/TRY)
   * before responding. When omitted or === "TRY", the raw X/TRY
   * response is returned (backward compatibility for callers that
   * pre-date this field).
   *
   * The frontend NEVER does FX math after this commit; the backend
   * is the single source of truth.
   */
  functionalCurrency?: string;
}

export async function fetchFxRates(
  req: FxRatesRequest,
  options?: { signal?: AbortSignal },
): Promise<FxRatesApiResponse> {
  // Phase 6e.4.1 — only include `currencies` when it's a non-empty
  // array. An empty array is a 400 on the backend (we never want to
  // send one); `undefined` triggers ALL mode.
  const body: Record<string, unknown> = {
    startDate: req.startDate,
    endDate: req.endDate,
  };
  if (req.currencies && req.currencies.length > 0) {
    body.currencies = req.currencies;
  }
  // Phase 6e.4.4 — pass `functionalCurrency` through verbatim. The
  // backend tolerates absence (raw X/TRY) and case-insensitive
  // values; we send the trimmed string and let the backend
  // normalise.
  if (
    typeof req.functionalCurrency === 'string' &&
    req.functionalCurrency.trim().length > 0
  ) {
    body.functionalCurrency = req.functionalCurrency.trim();
  }
  return apiFetch<FxRatesApiResponse>(
    '/api/fx/rates',
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
    options,
  );
}
